import { ICachedFieldState } from "@odata/Data.utils";
import { forceRepaintElement, isSafariBrowser } from "@utils/dom.utils";
import { isDefined } from "@utils/general";
import { throttle } from "lodash";
import React from "react";

import { FieldType } from "../../../enums";
import BindingContext from "../../../odata/BindingContext";
import { FormStorage } from "../../../views/formView/FormStorage";
import { getInfoValue, IFieldDef, isFieldDisabledWithoutLock, isVisible } from "../FieldInfo";
import { ISmartFieldBlur, ISmartFieldChange, ISmartFieldTempDataActionArgs } from "../smartField/SmartField";
import { IFormGroupDef } from "./SmartFormGroup";

export interface ISmartFormGroupBaseProps {
    bindingContext: BindingContext;
    def: IFormGroupDef;
    title?: string;

    storage: FormStorage;

    isSimple?: boolean;
    isSkeleton?: boolean;

    onChange: (args: ISmartFieldChange) => void;
    onTemporalChange?: (args: ISmartFieldChange) => void;
    onCancel?: (args: ISmartFieldTempDataActionArgs) => void;
    onConfirm?: (args: ISmartFieldTempDataActionArgs) => void;
    onFieldStateChange?: (bc: BindingContext, state: ICachedFieldState, prevState: ICachedFieldState) => void;
    onBlur?: (args: ISmartFieldBlur) => void;
}


export default abstract class SmartFormGroupBase<T extends ISmartFormGroupBaseProps, S = {}> extends React.Component<T, S> {
    _ref = React.createRef<HTMLDivElement>();

    getFieldDefinition(rowItem: IFieldDef): IFieldDef {
        let path = rowItem.id;

        if (this.props.def.collection) {
            path = `${this.props.def.collection}/${path}`;
        }

        if (this.props.isSkeleton) {
            // in skeleton mode, we don't care about loaded data, we just want to use the definition
            const def = this.props.storage.data.definition?.fieldDefinition?.[path] ?? {};
            return {
                ...rowItem,
                ...def
                // todo asi to nebude univerzalni, kdesi ve SmartFormGroupe se to vola jeste nejak jinak pro lineitemy
                // // @ts-ignore
                // bindingContext: this.props.bindingContext.navigate(rowItem.id)
            };
        }

        return this.props.storage.data.mergedDefinition?.[path]?.fieldDef || rowItem;
    }

    repaintFieldsWrapper = throttle((): void => {
        if (this._ref.current) {
            forceRepaintElement(this._ref.current);
        }
    }, 100);

    handleBlur = async (args: ISmartFieldBlur): Promise<void> => {
        args.groupId = this.props.def.id;
        await this.props.onBlur?.(args);
    };

    handleChange = (e: ISmartFieldChange): void => {
        e.groupId = this.props.def.id;
        if (this.props.def.togglePropPath && e.bindingContext.getPath(true) === this.props.def.togglePropPath) {
            this.props.storage.addActiveGroupByKey(this.props.def.id);
        }
        this.props.onChange?.(e);
    };

    handleTemporalChange = (e: ISmartFieldChange): void => {
        e.groupId = this.props.def.id;
        this.props.onTemporalChange?.(e);
    };

    handleCancel = (args: ISmartFieldTempDataActionArgs): void => {
        this.props.onCancel?.({
            bindingContext: args.bindingContext,
            groupId: this.props.def.id
        });
    };

    handleFieldStateChange = (bc: BindingContext, state: ICachedFieldState, prevState: ICachedFieldState): void => {
        if (this._ref.current && isSafariBrowser() && state.isVisible !== prevState.isVisible) {
            this.repaintFieldsWrapper();
        }
        this.props.onFieldStateChange?.(bc, state, prevState);
    };

    handleConfirm = (args: ISmartFieldTempDataActionArgs): void => {
        this.props.onConfirm?.({
            bindingContext: args.bindingContext,
            groupId: this.props.def.id
        });
    };

    getCollapsedFieldsVisibility(def: IFieldDef): {
        hasVisibleCollapsedFields: boolean,
        hasEnabledCollapsedFields: boolean,
        hasEditableFields: boolean
    } {
        if (this.props.isSkeleton) {
            return {
                hasVisibleCollapsedFields: true,
                hasEnabledCollapsedFields: true,
                hasEditableFields: true
            };
        }

        const modelBc = this.props.storage.data.bindingContext;
        const hasVisibleCollapsedFields = !!def.collapsedRows?.find(collapsedRow => {
            return !!collapsedRow.find(field => {
                const fieldInfo = this.props.storage.getInfo(modelBc.navigate(field.id));
                return isVisible({
                    info: fieldInfo,
                    storage: this.props.storage,
                    bindingContext: fieldInfo.bindingContext
                });
            });
        });
        const hasEnabledCollapsedFields = hasVisibleCollapsedFields && !!def.collapsedRows?.find(collapsedRow => {
            return !!collapsedRow.find(field => {
                const bc = modelBc.navigate(field.id);
                const fieldInfo = this.props.storage.getInfo(bc);
                return !isFieldDisabledWithoutLock(fieldInfo, this.props.storage, bc);
            });
        });

        const hasEditableFields = !!def.collapsedRows?.find(collapsedRow => {
            return !!collapsedRow.find(field => {
                const fieldInfo = this.props.storage.getInfo(modelBc.navigate(field.id));
                return !getInfoValue(fieldInfo, "isReadOnly", {
                    info: fieldInfo,
                    storage: this.props.storage,
                    bindingContext: fieldInfo.bindingContext
                });
            });
        });

        return {
            hasVisibleCollapsedFields,
            hasEnabledCollapsedFields,
            hasEditableFields
        };
    }

    getFieldsVisibility(): boolean {
        const { storage, def } = this.props;
        const modelBc = storage.data.bindingContext;
        const rootBc = this.props.def.collection ? modelBc.navigate(this.props.def.collection) : modelBc;
        const isVisibleArgs = {
            storage,
            context: storage.context
        };

        // Must navigate from group's bindingContext, not storage root — group may be nested inside a collection.
        const lineItemsCollection = this.props.def.lineItems
            ? this.props.storage.getValue(this.props.bindingContext.navigate(this.props.def.lineItems.collection))
            : null;
        return (!!def.togglePropPath && isVisible({ bindingContext: storage.data.bindingContext.navigate(def.togglePropPath), ...isVisibleArgs })) // visible toggle switch in group header
            || (!!this.props.def.lineItems && this.props.def.lineItems?.isGroupToggleable === true) // visible toggle switch in line items group header
            || (!!this.props.def.lineItems && (!storage.isReadOnly || lineItemsCollection?.length > 0))
            || (!this.props.def.lineItems && !this.props.def.rows) || !!this.props.def.rows?.find(row => {
                return !!row.find(field => {
                    const bc = rootBc.navigate(field.id);
                    const fieldInfo = storage.getInfo(bc);
                    const visible = isVisible({
                        bindingContext: bc,
                        info: fieldInfo,
                        ...isVisibleArgs
                    });

                    if (fieldInfo.type !== FieldType.EditableText) {
                        return visible;
                    }

                    // hide group, if PageViewMode.FormReadOnly and only empty EditableText is inside
                    return visible && (!storage.isReadOnly || isDefined(this.props.storage.getValue(bc)));
                });
            });
    }
}
